import {
  CheckCircle2,
  Cloud,
  KeyRound,
  PowerOff,
  RotateCw,
  TriangleAlert,
  type LucideProps,
} from 'lucide-react'
import type { ComponentType } from 'react'

import { useCatalogServices, useProviders } from '@/api/hooks'
import type { ProviderInfo } from '@/api/types'
import { EmptyState, IconButton, Skeleton } from '@/components/ui'
import { generalErrorOf } from '@/features/auth/authErrors'
import { cn } from '@/lib/cn'

import { Callout, type CalloutTone } from './Callout'
import { CredentialForm } from './CredentialForm'
import { SettingsSection } from './SettingsSection'

type Health = ProviderInfo['health']

interface HealthStyle {
  label: string
  dot: string
  tone: CalloutTone
  icon: ComponentType<LucideProps>
  /** Whether the message deserves a callout rather than a plain line. */
  emphasize: boolean
}

const HEALTH: Record<Health, HealthStyle> = {
  available: {
    label: 'Available',
    dot: 'bg-success',
    tone: 'success',
    icon: CheckCircle2,
    emphasize: false,
  },
  degraded: {
    label: 'Degraded',
    dot: 'bg-warning',
    tone: 'warning',
    icon: TriangleAlert,
    emphasize: true,
  },
  misconfigured: {
    label: 'Needs setup',
    dot: 'bg-danger',
    tone: 'danger',
    icon: KeyRound,
    emphasize: true,
  },
  disabled: {
    label: 'Disabled',
    dot: 'bg-text-subtle',
    tone: 'info',
    icon: PowerOff,
    emphasize: true,
  },
}

function ProviderCard({ provider }: { provider: ProviderInfo }) {
  const style = HEALTH[provider.health]
  const ready = provider.can_download && provider.health !== 'disabled' && provider.health !== 'misconfigured'

  return (
    <li className="rounded-lg border border-border bg-surface p-3">
      <div className="flex items-center gap-2.5">
        <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', style.dot)} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-text">
          {provider.name}
        </span>
        <span className="shrink-0 text-xs font-medium text-text-muted">{ready ? 'Ready to acquire' : 'Acquisition unavailable'}</span>
      </div>

      {provider.message ? (
        style.emphasize ? (
          <Callout tone={style.tone} icon={style.icon} className="mt-2.5">
            {provider.message}
          </Callout>
        ) : (
          <p className="mt-1.5 text-sm text-text-muted">{provider.message}</p>
        )
      ) : null}

      {provider.requires_credentials ? <CredentialForm provider={provider} /> : null}
    </li>
  )
}

function ProvidersSkeleton() {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Loading music sources">
      {Array.from({ length: 2 }, (_, index) => (
        <Skeleton key={index} className="h-[6.5rem] w-full rounded-lg" />
      ))}
    </div>
  )
}

export function ProvidersSection() {
  const { data: providers, isPending, isError, error, refetch, isFetching } = useProviders()

  const catalog = useCatalogServices()

  const needsAttention = (providers ?? []).filter(
    (provider) => provider.health !== 'available',
  ).length

  return (
    <SettingsSection
      icon={Cloud}
      title="Audio providers"
      description={
        needsAttention > 0
          ? `${needsAttention} ${needsAttention === 1 ? 'audio provider needs' : 'audio providers need'} attention.`
          : undefined
      }
      headerAction={
        <IconButton
          aria-label="Re-check music sources"
          loading={isFetching && !isPending}
          onClick={() => void refetch()}
        >
          <RotateCw className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      }
    >
      <p className="mb-4 text-sm text-text-muted">Audio providers acquire files. Your library, playlists and stored metadata remain available independently.</p>
      <div className="mb-5 rounded-lg border border-border p-3">
        <h3 className="text-sm font-medium text-text">Catalog and discovery</h3>
        <p className="mt-1 text-sm text-text-muted">Local browsing and recommendations are always available. External enrichment is optional.</p>
        {catalog.isPending ? <p className="mt-2 text-sm text-text-muted">Loading enrichment status…</p> : catalog.isError ? (
          <p className="mt-2 text-sm text-text-muted">Enrichment status could not be loaded. Your local library remains available.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {(catalog.data?.metadata ?? []).map((service) => <li key={`metadata:${service.slug}`}>{service.name}: {service.health}</li>)}
            {(catalog.data?.discovery ?? []).map((service) => <li key={`discovery:${service.slug}`}>{service.name}: {service.health}</li>)}
            {!(catalog.data?.metadata ?? []).some((service) => service.slug !== 'library') && !(catalog.data?.discovery ?? []).some((service) => service.slug !== 'library') ? <li className="text-text-muted">External enrichment is disabled.</li> : null}
          </ul>
        )}
      </div>
      {isPending ? <ProvidersSkeleton /> : null}

      {!isPending && isError ? (
        <Callout tone="danger" title="Audio provider status is unavailable">
          {generalErrorOf(error) ?? 'The server did not answer. Try again in a moment.'}
        </Callout>
      ) : null}

      {!isPending && !isError && (providers?.length ?? 0) === 0 ? (
        <EmptyState
          icon={Cloud}
          title="No audio providers configured"
          description="Import, search and play your library without a provider account. Enable a provider only to acquire new files."
          className="py-10"
        />
      ) : null}

      {!isPending && providers && providers.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {providers.map((provider) => (
            <ProviderCard key={provider.slug} provider={provider} />
          ))}
        </ul>
      ) : null}
    </SettingsSection>
  )
}
